import { AfterViewInit, Component, HostListener, Input, OnInit } from '@angular/core';

declare let Bokeh: any;

interface ChartTheme {
  background: string;
  gridLine: string;
  plotBackground: string;
  gridLineAlpha: number;
  border: string;
  lactate: ChartMetric;
  vo2: ChartMetric;
  bars: ChartBars;
}

interface ChartMetric {
  line: string;
  dots: string;
  axis: string;
  error: string;
}

interface ChartBars {
  fill: string;
  fillAlpha: number;
  stroke: string;
  strokeAlpha: number;
  text: string;
  timeLabel: string;
}

interface TimeRange {
  start: number;
  end: number;
}

interface ChartRange {
  start: number;
  end: number;
}

interface UnifiedRanges {
  lactateRange: ChartRange;
  vo2Range: ChartRange;
  lactateGridTicks: number[];
  vo2GridTicks: number[];
}

interface TimeTicksResult {
  left: number[];
  right: number[];
  interval: number;
}

interface ChartSpaces {
  gap: number;
  leftAxis: number;
  rightAxis: number;
}

interface Bar {
  start: number;
  end: number;
  vo2tot: number;
  value: string;
  unit: string;
  time: string;
}

interface DataConfig {
  duration: number[];
  vo2_values: number[];
  lactate_values: number[];
  vo2_dots_x?: number[];
  vo2_dots_y?: number[];
  vo2_dots_error?: number[][];
  lactate_dots_x?: number[];
  lactate_dots_y?: number[];
  lactate_dots_error?: number[][];
  bars: Bar[];
}

interface GraphConfig {
  left: DataConfig;
  right: DataConfig;
}

@Component({
  selector: 'app-kinetics-graph-step-test',
  templateUrl: './kinetics-graph-step-test.component.html',
  styleUrls: ['./kinetics-graph-step-test.component.scss']
})
export class KineticsGraphStepTestComponent implements AfterViewInit, OnInit {
  @Input() config!: any;

  private readonly CHART_HEIGHT: number = 500;
  private readonly CONTAINER_WIDTH_RATIO: number = 1.02;
  private readonly CHART_SPACING: ChartSpaces = {
    gap: 10,
    leftAxis: 30,
    rightAxis: 30
  };
  private readonly MIN_CHART_WIDTH: number = 200;
  private readonly RESIZE_DEBOUNCE_MS: number = 250;
  private readonly INITIAL_RENDER_DELAY_MS: number = 100;
  private readonly LACTATE_STEP: number = 2;
  private readonly MIN_LACTATE_RANGE: number = 6;
  private readonly VO2_PADDING: number = 5;
  private readonly RANGE_PADDING_RATIO: number = 0.2;
  private readonly TIME_PADDING_RATIO: number = 0.05;

  private readonly THEMES: Record<string, ChartTheme> = {
    light: {
      background: '#fbfbfb',
      plotBackground: '#fbfbfb',
      gridLine: '#e0e0e0',
      gridLineAlpha: 0.8,
      border: '#000000',
      lactate: {
        line: '#ff0000',
        dots: '#ff0000',
        axis: '#111111',
        error: '#ff0000'
      },
      vo2: {
        line: '#0000ff',
        dots: '#0000ff',
        axis: '#111111',
        error: '#0066cc'
      },
      bars: {
        fill: '#d3d3d3',
        fillAlpha: 0.3,
        stroke: '#808080',
        strokeAlpha: 0.5,
        text: '#000000',
        timeLabel: '#000000'
      }
    },
    dark: {
      background: '#282929',
      plotBackground: '#000000',
      gridLine: '#555555',
      gridLineAlpha: 0.6,
      border: '#666666',
      lactate: {
        line: '#ff6b6b',
        dots: '#ff6b6b',
        axis: '#aaaaaa',
        error: '#ff6b6b'
      },
      vo2: {
        line: '#4dabf7',
        dots: '#4dabf7',
        axis: '#aaaaaa',
        error: '#74c0fc'
      },
      bars: {
        fill: '#404040',
        fillAlpha: 0.4,
        stroke: '#707070',
        strokeAlpha: 0.7,
        text: '#ffffff',
        timeLabel: '#cccccc'
      }
    }
  };

  public theme: string = (localStorage.getItem('theme') || 'light').toLowerCase();
  public chartHeight: number;

  private containerWidth: number = 0;
  private leftChartWidth: number = 0;
  private rightChartWidth: number = 0;
  private pixelsPerSecond: number = 1;
  private leftPlot: any = null;
  private rightPlot: any = null;
  private resizeTimeout: any;

  private get currentTheme(): ChartTheme {
    return this.THEMES[this.theme];
  }

  ngOnInit(): void {
    this.chartHeight = this.config?.left?.chart_height;
    this.updateDimensions();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.updateDimensions();
      this.createKineticsStepTestGraph();
    }, this.INITIAL_RENDER_DELAY_MS);
  }

  @HostListener('window:resize')
  onResize(): void {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      const oldWidth: number = this.containerWidth;
      this.updateDimensions();
      if (this.containerWidth !== oldWidth) {
        this.createKineticsStepTestGraph();
      }
    }, this.RESIZE_DEBOUNCE_MS);
  }

  setTheme(newTheme: 'light' | 'dark'): void {
    if (this.theme !== newTheme) {
      this.theme = newTheme;
      this.createKineticsStepTestGraph();
    }
  }

  toggleTheme(): void {
    this.setTheme(this.theme === 'light' ? 'dark' : 'light');
  }

  private updateDimensions(): void {
    const container = document.querySelector('.charts-container') as HTMLElement;
    if (container) {
      this.containerWidth = container.offsetWidth;
      this.calculateAdaptiveChartSizes();
    }
  }

  private calculateAdaptiveChartSizes(): void {
    if (!this.config?.left || !this.config?.right) {
      return;
    }

    const totalWidth: number = this.containerWidth * this.CONTAINER_WIDTH_RATIO;
    const availableWidth: number = totalWidth - this.CHART_SPACING.gap;

    const timeSpans = {
      left: this.getTimeSpan(this.config.left),
      right: this.getTimeSpan(this.config.right)
    };

    const totalTimeSpan: number = timeSpans.left + timeSpans.right;
    const leftRatio: number = timeSpans.left / totalTimeSpan;
    const rightRatio: number = timeSpans.right / totalTimeSpan;

    this.leftChartWidth = Math.floor(availableWidth * leftRatio);
    this.rightChartWidth = Math.floor(availableWidth * rightRatio);

    this.leftChartWidth = Math.max(this.leftChartWidth, this.MIN_CHART_WIDTH);
    this.rightChartWidth = Math.max(this.rightChartWidth, this.MIN_CHART_WIDTH);

    const totalCalculated: number = this.leftChartWidth + this.rightChartWidth;
    if (totalCalculated > availableWidth) {
      const scale: number = availableWidth / totalCalculated;
      this.leftChartWidth = Math.floor(this.leftChartWidth * scale);
      this.rightChartWidth = Math.floor(this.rightChartWidth * scale);
    }
  }

  private getTimeSpan(dataConfig: DataConfig): number {
    const durationSpan: number = dataConfig.duration?.length
      ? Math.max(...dataConfig.duration) - Math.min(...dataConfig.duration)
      : 0;

    const barSpan: number = dataConfig.bars?.length
      ? Math.max(...dataConfig.bars.map((bar: Bar) => bar.end)) -
      Math.min(...dataConfig.bars.map((bar: Bar) => bar.start))
      : 0;

    return Math.max(durationSpan, barSpan);
  }

  private getTimeRange(config: DataConfig): TimeRange {
    const allX: number[] = [
      ...config.duration,
      ...config.bars.flatMap((bar: Bar) => [bar.start, bar.end])
    ];
    const minX: number = Math.min(...allX);
    const maxX: number = Math.max(...allX);
    const padding: number = (maxX - minX) * this.TIME_PADDING_RATIO;

    return {
      start: Math.max(0, minX - padding),
      end: maxX + padding
    };
  }

  private getData(config: DataConfig, key: string): number[] {
    return (config as any)[key] || [];
  }

  private getErrorValues(config: DataConfig, type: string): number[] {
    const values: number[] = [];
    const errorKey = `${type}_dots_error`;
    const yKey = `${type}_dots_y`;

    if ((config as any)[errorKey]?.length) {
      (config as any)[yKey].forEach((y: number, i: number) => {
        const err = (config as any)[errorKey][i];
        if (Array.isArray(err)) {
          values.push(y + Math.abs(err[0] || 0), y - Math.abs(err[1] || 0));
        }
      });
    }

    return values;
  }

  private calculateUnifiedRanges(): UnifiedRanges {
    const left: DataConfig = this.config.left;
    const right: DataConfig = this.config.right;

    const lactateValues: number[] = [
      ...this.getData(left, 'lactate_values'),
      ...this.getData(left, 'lactate_dots_y'),
      ...this.getData(right, 'lactate_values'),
      ...this.getData(right, 'lactate_dots_y'),
      ...this.getErrorValues(left, 'lactate'),
      ...this.getErrorValues(right, 'lactate')
    ];

    const vo2Values: number[] = [
      ...this.getData(left, 'vo2_values'),
      ...this.getData(right, 'vo2_values'),
      ...this.getData(left, 'vo2_dots_y'),
      ...this.getData(right, 'vo2_dots_y'),
      ...(left.bars?.map((bar: Bar) => bar.vo2tot) || []),
      ...(right.bars?.map((bar: Bar) => bar.vo2tot) || []),
      ...this.getErrorValues(left, 'vo2'),
      ...this.getErrorValues(right, 'vo2')
    ];

    const lactateRange: ChartRange = this.createLactateRange(lactateValues);
    const vo2Range: ChartRange = this.createVO2Range(vo2Values, this.VO2_PADDING);

    const lactateGridTicks: number[] = this.generateLactateGridTicks(
      lactateRange.start,
      lactateRange.end
    );

    const vo2GridTicks: number[] = this.generateProportionalVO2Ticks(
      lactateRange,
      vo2Range,
      lactateGridTicks
    );

    return { lactateRange, vo2Range, lactateGridTicks, vo2GridTicks };
  }

  private createLactateRange(values: number[]): ChartRange {
    const max: number = Math.max(...values);
    const maxRounded: number = Math.ceil(max / this.LACTATE_STEP) * this.LACTATE_STEP;
    const endValue: number = Math.max(maxRounded, this.MIN_LACTATE_RANGE);

    return { start: 0, end: endValue };
  }

  private createVO2Range(values: number[], padding: number): ChartRange {
    const min: number = Math.min(...values);
    const max: number = Math.max(...values);
    const pad: number = Math.max((max - min) * this.RANGE_PADDING_RATIO, padding);

    return {
      start: Math.max(0, min - pad),
      end: max + pad
    };
  }

  private generateLactateGridTicks(min: number, max: number): number[] {
    const ticks: number[] = [];
    for (let tick = 0; tick <= max; tick += this.LACTATE_STEP) {
      ticks.push(tick);
    }
    return ticks;
  }

  private generateProportionalVO2Ticks(
    lactateRange: ChartRange,
    vo2Range: ChartRange,
    lactateGridTicks: number[]
  ): number[] {
    const lactateRangeSpan: number = lactateRange.end - lactateRange.start;
    const proportionalPositions: number[] = lactateGridTicks.map(
      (tick: number) => (tick - lactateRange.start) / lactateRangeSpan
    );

    const vo2RangeSpan: number = vo2Range.end - vo2Range.start;
    return proportionalPositions.map((proportion: number) => {
      const tick: number = vo2Range.start + proportion * vo2RangeSpan;
      return Math.round(tick * 1000) / 1000;
    });
  }

  private generateUnifiedTimeTicks(
    leftConfig: DataConfig,
    rightConfig: DataConfig
  ): TimeTicksResult {
    const leftTimeRange: TimeRange = this.getTimeRange(leftConfig);
    const rightTimeRange: TimeRange = this.getTimeRange(rightConfig);

    const totalTimeSpan: number = Math.max(
      leftTimeRange.end - leftTimeRange.start,
      rightTimeRange.end - rightTimeRange.start
    );

    const interval: number = this.determineTimeInterval(totalTimeSpan);

    const leftTicks: number[] = this.generateTicksForRange(leftTimeRange, interval);
    const rightTicks: number[] = this.generateTicksForRange(rightTimeRange, interval);

    return { left: leftTicks, right: rightTicks, interval };
  }

  private determineTimeInterval(totalTimeSpan: number): number {
    if (totalTimeSpan <= 300) {
      return 30;
    }
    if (totalTimeSpan <= 600) {
      return 60;
    }
    if (totalTimeSpan <= 1800) {
      return 120;
    }
    return 300;
  }

  private generateTicksForRange(range: TimeRange, interval: number): number[] {
    const ticks: number[] = [];
    const start: number = Math.ceil(range.start / interval) * interval;

    for (let tick = start; tick <= range.end; tick += interval) {
      ticks.push(tick);
    }

    return ticks;
  }

  private createTimeFormatter(): any {
    return new Bokeh.CustomJSTickFormatter({
      code: 'var m=Math.floor(tick/60),s=Math.floor(tick%60);return m+":"+(s<10?"0":"")+s;'
    });
  }

  private generateTimeTicks(minX: number, maxX: number, chartWidth: number): number[] {
    const range: number = maxX - minX;
    const maxTicks: number = Math.floor((chartWidth - 100) / 60);

    const intervals = [
      { max: 60, step: 10 },
      { max: 300, step: 30 },
      { max: 600, step: 60 },
      { max: 1800, step: 120 },
      { max: Infinity, step: 300 }
    ];

    const foundInterval = intervals.find((i) => range <= i.max)!;
    const interval: number = Math.max(
      foundInterval.step,
      Math.ceil(range / maxTicks / foundInterval.step) * foundInterval.step
    );

    const ticks: number[] = [];
    for (let tick = Math.ceil(minX / interval) * interval; tick <= maxX; tick += interval) {
      ticks.push(tick);
    }

    return ticks.length > maxTicks
      ? ticks.filter((_: number, i: number) => i % Math.ceil(ticks.length / maxTicks) === 0)
      : ticks;
  }

  private generateProportionalTicks(min: number, max: number, numIntervals: number): number[] {
    const step: number = (max - min) / numIntervals;
    const ticks: number[] = [];

    for (let i = 0; i <= numIntervals; i++) {
      const tick: number = min + step * i;
      ticks.push(Math.round(tick * 1000) / 1000);
    }

    return ticks;
  }

  private generateNiceGridTicks(min: number, max: number, targetCount: number): number[] {
    const range: number = max - min;
    const rawStep: number = range / (targetCount - 1);
    const magnitude: number = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const normalizedStep: number = rawStep / magnitude;

    let niceStep: number;
    if (normalizedStep <= 1) {
      niceStep = 1;
    } else if (normalizedStep <= 2) {
      niceStep = 2;
    } else if (normalizedStep <= 5) {
      niceStep = 5;
    } else {
      niceStep = 10;
    }

    const step: number = niceStep * magnitude;
    const ticks: number[] = [];
    const start: number = Math.floor(min / step) * step;

    for (let tick = start; tick <= max + step * 0.001; tick += step) {
      if (tick >= min) {
        ticks.push(Math.round(tick * 1000) / 1000);
      }
    }

    return ticks;
  }

  createKineticsStepTestGraph(): void {
    this.clearCharts();
    const ranges: UnifiedRanges = this.calculateUnifiedRanges();
    const timeTicksResult: TimeTicksResult = this.generateUnifiedTimeTicks(
      this.config.left,
      this.config.right
    );

    this.leftPlot = this.createChart(this.leftChartWidth, ranges, true, timeTicksResult.left);
    this.rightPlot = this.createChart(this.rightChartWidth, ranges, false, timeTicksResult.right);
  }

  private clearCharts(): void {
    const chartIds: string[] = ['left-chart', 'right-chart'];

    chartIds.forEach((id: string) => {
      const chart = document.getElementById(id);
      if (chart) {
        chart.innerHTML = '';
      }
    });

    const plots = [this.leftPlot, this.rightPlot];
    plots.forEach((plot: any) => {
      if (plot?.model) {
        try {
          plot.model.document.clear();
        } catch (e) {}
      }
    });

    this.leftPlot = null;
    this.rightPlot = null;
  }

  private createChart(
    width: number,
    ranges: UnifiedRanges,
    isLeft: boolean,
    timeTicks: number[]
  ): any {
    const theme: ChartTheme = this.currentTheme;
    const dataConfig: DataConfig = this.config[isLeft ? 'left' : 'right'];
    const timeRange: TimeRange = this.getTimeRange(dataConfig);

    const yRange: ChartRange = isLeft ? ranges.lactateRange : ranges.vo2Range;
    const extraRange: any = isLeft
      ? { vo2: new Bokeh.Range1d(ranges.vo2Range) }
      : { lactate: new Bokeh.Range1d(ranges.lactateRange) };

    const figure = this.createBokehFigure(
      width,
      timeRange,
      yRange,
      extraRange,
      theme,
      isLeft
    );

    this.configureFigureAxes(figure, ranges, timeTicks, theme, isLeft);
    this.renderBars(figure, dataConfig, theme, ranges, isLeft);
    this.renderLines(figure, dataConfig, theme, isLeft);
    this.renderDots(figure, dataConfig, theme, isLeft);

    return Bokeh.Plotting.show(figure, isLeft ? '#left-chart' : '#right-chart');
  }

  private createBokehFigure(
    width: number,
    timeRange: TimeRange,
    yRange: ChartRange,
    extraRange: any,
    theme: ChartTheme,
    isLeft: boolean
  ): any {
    return new Bokeh.Plotting.figure({
      width,
      height: this.chartHeight ?? this.CHART_HEIGHT,
      title: null,
      toolbar_location: null,
      tools: '',
      active_drag: null,
      active_scroll: null,
      active_tap: null,
      x_range: [timeRange.start, timeRange.end],
      y_range: [yRange.start, yRange.end],
      x_axis_label: '',
      y_axis_label: isLeft ? 'Lactate [mmol/l]' : null,
      y_axis_location: isLeft ? 'left' : null,
      background_fill_color: theme.plotBackground,
      border_fill_color: theme.background,
      outline_line_color: theme.gridLine,
      outline_line_alpha: theme.gridLineAlpha,
      extra_y_ranges: extraRange,
      min_border_left: 10,
      min_border_right: 10,
      min_border_top: 10,
      min_border_bottom: 40,
      sizing_mode: null
    });
  }

  private configureFigureAxes(
    figure: any,
    ranges: UnifiedRanges,
    timeTicks: number[],
    theme: ChartTheme,
    isLeft: boolean
  ): void {
    const mainYTicks: number[] = isLeft ? ranges.lactateGridTicks : ranges.vo2GridTicks;
    const mainYTicker = new Bokeh.FixedTicker({ ticks: mainYTicks });

    Object.assign(figure.grid, {
      grid_line_color: theme.gridLine,
      grid_line_alpha: theme.gridLineAlpha
    });

    figure.ygrid.ticker = mainYTicker;
    figure.xaxis.formatter = this.createTimeFormatter();

    const xTicker = new Bokeh.FixedTicker({ ticks: timeTicks });
    figure.xaxis.ticker = xTicker;
    figure.xgrid.ticker = xTicker;

    this.applyXAxisTheme(figure, theme);

    if (isLeft) {
      this.configureLeftChartAxes(figure, ranges, mainYTicker, theme);
    } else {
      this.configureRightChartAxes(figure, ranges, mainYTicker, theme);
    }
  }

  private applyXAxisTheme(figure: any, theme: ChartTheme): void {
    this.applyAxisTheme(figure.xaxis, theme.border, true);
    figure.xaxis.major_label_text_color = this.theme === 'light' ? '#000000' : '#ffffff';
  }

  private configureLeftChartAxes(
    figure: any,
    ranges: UnifiedRanges,
    mainYTicker: any,
    theme: ChartTheme
  ): void {
    figure.yaxis.ticker = mainYTicker;
    this.applyAxisTheme(figure.yaxis, theme.lactate.axis, true);

    const vo2Axis = new Bokeh.LinearAxis({
      y_range_name: 'vo2',
      ticker: new Bokeh.FixedTicker({ ticks: ranges.vo2GridTicks })
    });
    figure.add_layout(vo2Axis, 'left');
    vo2Axis.visible = false;
  }

  private configureRightChartAxes(
    figure: any,
    ranges: UnifiedRanges,
    mainYTicker: any,
    theme: ChartTheme
  ): void {
    figure.yaxis.ticker = mainYTicker;

    const lactateAxis = new Bokeh.LinearAxis({
      y_range_name: 'lactate',
      ticker: new Bokeh.FixedTicker({ ticks: ranges.lactateGridTicks })
    });
    figure.add_layout(lactateAxis, 'left');
    lactateAxis.visible = false;

    const rightAxis = new Bokeh.LinearAxis({ axis_label: 'VO2 [ml/min/kg]' });
    figure.add_layout(rightAxis, 'right');
    this.applyAxisTheme(rightAxis, theme.vo2.axis, true);
  }

  private applyAxisTheme(axis: any, color: string, forceBlackLines: boolean = false): void {
    const theme: ChartTheme = this.currentTheme;

    Object.assign(axis, {
      axis_label_text_color: color,
      major_label_text_color: color,
      axis_label_text_font_size: this.scaledFontPt(10),
      major_label_text_font_size: this.scaledFontPt(8),
      axis_line_color: forceBlackLines ? '#000000' : theme.border,
      major_tick_line_color: forceBlackLines ? '#000000' : theme.border,
      axis_label_text_font_style: 'normal'
    });
  }

  private renderBars(
    figure: any,
    dataConfig: DataConfig,
    theme: ChartTheme,
    ranges: UnifiedRanges,
    isLeft: boolean
  ): void {
    dataConfig.bars?.forEach((bar: Bar) => {
      const center: number = (bar.start + bar.end) / 2;
      const height: number = bar.vo2tot;
      const width: number = isLeft
        ? bar.end - bar.start
        : bar.end - bar.start + 8 / this.pixelsPerSecond;
      const yRange: string | undefined = isLeft ? 'vo2' : undefined;

      this.renderBarRectangle(figure, center, height, width, theme, yRange);
      this.renderBarValueText(figure, center, height, bar, theme, yRange);
      this.renderBarTimeLabel(figure, center, bar, ranges, theme, yRange, isLeft);
    });
  }

  private renderBarRectangle(
    figure: any,
    center: number,
    height: number,
    width: number,
    theme: ChartTheme,
    yRange?: string
  ): void {
    figure.rect([center], [height / 2], [width], [height], {
      fill_color: theme.bars.fill,
      fill_alpha: theme.bars.fillAlpha,
      line_color: theme.bars.stroke,
      line_alpha: theme.bars.strokeAlpha,
      line_width: 1,
      y_range_name: yRange
    });
  }

  private renderBarValueText(
    figure: any,
    center: number,
    height: number,
    bar: Bar,
    theme: ChartTheme,
    yRange?: string
  ): void {
    figure.text([center], [height / 2], [bar.value + bar.unit], {
      text_align: 'center',
      text_baseline: 'middle',
      text_font_size: this.scaledFontPt(10),
      text_color: theme.bars.text,
      y_range_name: yRange
    });
  }

  private renderBarTimeLabel(
    figure: any,
    center: number,
    bar: Bar,
    ranges: UnifiedRanges,
    theme: ChartTheme,
    yRange: string | undefined,
    isLeft: boolean
  ): void {
    const yPosition: number = ranges[isLeft ? 'vo2Range' : 'vo2Range'].start;

    figure.text([center], [yPosition], [bar.time], {
      text_align: 'center',
      text_baseline: 'bottom',
      text_font_size: this.scaledFontPt(9),
      text_color: theme.bars.timeLabel,
      y_range_name: yRange
    });
  }

  private renderLines(figure: any, dataConfig: DataConfig, theme: ChartTheme, isLeft: boolean): void {
    const vo2Range: string | undefined = isLeft ? 'vo2' : undefined;
    const lactateRange: string | undefined = isLeft ? undefined : 'lactate';

    this.renderVO2Line(figure, dataConfig, theme, vo2Range);
    this.renderLactateLine(figure, dataConfig, theme, lactateRange);
  }

  private renderVO2Line(
    figure: any,
    dataConfig: DataConfig,
    theme: ChartTheme,
    vo2Range?: string
  ): void {
    figure.line(dataConfig.duration, dataConfig.vo2_values, {
      line_width: 2,
      line_color: theme.vo2.line,
      line_alpha: 0.8,
      y_range_name: vo2Range
    });
  }

  private renderLactateLine(
    figure: any,
    dataConfig: DataConfig,
    theme: ChartTheme,
    lactateRange?: string
  ): void {
    figure.line(dataConfig.duration, dataConfig.lactate_values, {
      line_width: 2,
      line_color: theme.lactate.line,
      line_alpha: 0.8,
      y_range_name: lactateRange
    });
  }

  private renderDots(
    figure: any,
    dataConfig: DataConfig,
    theme: ChartTheme,
    isLeft: boolean
  ): void {
    const vo2Range: string | undefined = isLeft ? 'vo2' : undefined;
    const lactateRange: string | undefined = isLeft ? undefined : 'lactate';

    this.renderVO2Dots(figure, dataConfig, theme, vo2Range);
    this.renderLactateDots(figure, dataConfig, theme, lactateRange);
  }

  private renderVO2Dots(
    figure: any,
    dataConfig: DataConfig,
    theme: ChartTheme,
    vo2Range?: string
  ): void {
    if (!dataConfig.vo2_dots_x?.length) {
      return;
    }

    dataConfig.vo2_dots_x.forEach((x: number, i: number) => {
      const y: number = dataConfig.vo2_dots_y![i];

      if (dataConfig.vo2_dots_error?.[i]) {
        this.renderVO2ErrorBars(figure, x, y, dataConfig.vo2_dots_error[i], theme, vo2Range);
      }

      this.renderVO2DotCircle(figure, x, y, theme, vo2Range);
    });
  }

  private renderVO2ErrorBars(
    figure: any,
    x: number,
    y: number,
    error: number[],
    theme: ChartTheme,
    vo2Range?: string
  ): void {
    const errUp: number = Math.abs(error[0] || 0);
    const errDown: number = Math.abs(error[1] || 0);
    const errHor: number = Math.abs(error[2] || 0);

    if (errUp > 0) {
      this.renderVerticalErrorBar(figure, x, y, y + errUp, errHor, theme.vo2.error, vo2Range);
    }

    if (errDown > 0) {
      this.renderVerticalErrorBar(figure, x, y, y - errDown, errHor, theme.vo2.error, vo2Range);
    }

    if (errHor > 0) {
      this.renderHorizontalErrorBar(figure, x, y, errHor, errUp, errDown, theme.vo2.error, vo2Range);
    }
  }

  private renderVO2DotCircle(
    figure: any,
    x: number,
    y: number,
    theme: ChartTheme,
    vo2Range?: string
  ): void {
    const radiusInPixels = 5;
    const xRange = figure.x_range.end - figure.x_range.start;
    const chartWidth = figure.width;
    const radiusInDataUnits = (xRange / chartWidth) * radiusInPixels;

    figure.circle([x], [y], radiusInDataUnits, {
      fill_color: theme.vo2.dots,
      line_color: theme.vo2.dots,
      fill_alpha: 0.5,
      line_alpha: 0.5,
      y_range_name: vo2Range
    });
  }

  private renderLactateDots(
    figure: any,
    dataConfig: DataConfig,
    theme: ChartTheme,
    lactateRange?: string
  ): void {
    if (!dataConfig.lactate_dots_x?.length) {
      return;
    }

    dataConfig.lactate_dots_x.forEach((x: number, i: number) => {
      const y: number = dataConfig.lactate_dots_y![i];

      if (dataConfig.lactate_dots_error?.[i]) {
        this.renderLactateErrorBars(figure, x, y, dataConfig.lactate_dots_error[i], theme, lactateRange);
      }

      this.renderLactateDotCircle(figure, x, y, theme, lactateRange);
    });
  }

  private renderLactateErrorBars(
    figure: any,
    x: number,
    y: number,
    error: number[],
    theme: ChartTheme,
    lactateRange?: string
  ): void {
    const errUp: number = Math.abs(error[0] || 0);
    const errDown: number = Math.abs(error[1] || 0);
    const errHor: number = Math.abs(error[2] || 0);

    if (errUp > 0) {
      this.renderVerticalErrorBar(figure, x, y, y + errUp, errHor, theme.lactate.error, lactateRange);
    }

    if (errDown > 0) {
      this.renderVerticalErrorBar(figure, x, y, y - errDown, errHor, theme.lactate.error, lactateRange);
    }

    if (errHor > 0) {
      this.renderHorizontalErrorBar(figure, x, y, errHor, errUp, errDown, theme.lactate.error, lactateRange);
    }
  }

  private renderLactateDotCircle(
    figure: any,
    x: number,
    y: number,
    theme: ChartTheme,
    lactateRange?: string
  ): void {
    const radiusInPixels = 3;
    const xRange = figure.x_range.end - figure.x_range.start;
    const chartWidth = figure.width;
    const radiusInDataUnits = (xRange / chartWidth) * radiusInPixels;

    figure.circle([x], [y], radiusInDataUnits, {
      fill_color: theme.lactate.dots,
      line_color: theme.lactate.dots,
      fill_alpha: 0.8,
      line_alpha: 0.8,
      y_range_name: lactateRange
    });
  }

  private renderVerticalErrorBar(
    figure: any,
    x: number,
    y1: number,
    y2: number,
    errHor: number,
    color: string,
    yRange?: string
  ): void {
    figure.line([x, x], [y1, y2], {
      line_width: 2,
      line_color: color,
      y_range_name: yRange
    });

    figure.line([x - errHor / 2, x + errHor / 2], [y2, y2], {
      line_width: 2,
      line_color: color,
      y_range_name: yRange
    });
  }

  private renderHorizontalErrorBar(
    figure: any,
    x: number,
    y: number,
    errHor: number,
    errUp: number,
    errDown: number,
    color: string,
    yRange?: string
  ): void {
    figure.line([x - errHor, x + errHor], [y, y], {
      line_width: 2,
      line_color: color,
      y_range_name: yRange
    });

    const cap: number = Math.max(errUp, errDown) * 0.2;
    const xPositions: number[] = [x - errHor, x + errHor];

    xPositions.forEach((xPos: number) => {
      figure.line([xPos, xPos], [y - cap, y + cap], {
        line_width: 2,
        line_color: color,
        y_range_name: yRange
      });
    });
  }

  private scaledFontPt(fontSize: number): string {
    const fontSizeScale = this.chartHeight ? this.chartHeight / 500 : 1;
    return `${Math.ceil(fontSizeScale * fontSize)}pt`
  }
}
